import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from 'react-query';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Fade,
  Slide,
  useTheme,
  IconButton,
  Stack,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  Close as CloseIcon,
  CheckCircle,
  Description,
  VideoFile,
  Folder
} from '@mui/icons-material';
import client from '../../API';
import { getFileType } from '../../Utils/constants';

const fileTypeIcons = {
  brief: Description,
  contract: Description,
  image: ImageIcon,
  video: VideoFile,
  file: InsertDriveFile,
  pdf: PictureAsPdf
};

const fileTypeColors = {
  brief: '#2196F3',
  contract: '#4CAF50',
  image: '#FF9800',
  video: '#9C27B0',
  file: '#607D8B'
};

const renderFilePreview = (url, fileName) => {
  const fileType = getFileType(url);
  if (fileType === 'image') {
    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          width: 120,
          height: 120,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: '2px solid rgba(255,255,255,0.2)'
        }}
      >
        <img
          src={url}
          alt={fileName}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(76,175,80,0.1), rgba(33,150,243,0.1))'
          }}
        />
      </Box>
    );
  }

  const Icon = fileTypeIcons[fileType] || InsertDriveFile;
  
  return (
    <Box
      sx={{
        width: 120,
        height: 120,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: '2px solid rgba(255,255,255,0.2)'
      }}
    >
      <Icon sx={{ fontSize: 48, color: 'white', opacity: 0.9 }} />
    </Box>
  );
};

const UploadFileDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const FILE_SIZE_LIMIT = 4.5 * 1024 * 1024;

  const fileTypes = [
    { value: 'brief', label: 'Brief', icon: Description },
    { value: 'contract', label: 'Contract', icon: Description },
    { value: 'image', label: 'Image', icon: ImageIcon },
    { value: 'video', label: 'Video', icon: VideoFile },
    { value: 'file', label: 'Document', icon: Folder }
  ];

  const { mutate: uploadFile, isLoading } = useMutation(
    (fileData) => client.files.post(fileData),
    {
      onSuccess: (data) => {
        setUploadedFile(data);
        setError(null);
        setUploadProgress(100);
      },
      onError: (error) => {
        setError(error.message || 'Failed to upload file');
        setUploadProgress(0);
      }
    }
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      setError(null);
      setUploadProgress(0);

      if (!documentType) {
        setError('Please select a file type first');
        return;
      }

      if (file.size > FILE_SIZE_LIMIT) {
        setError('File size exceeds the 4.5MB limit. Please upload a smaller file.');
        return;
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 200);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64File = reader.result.split(',')[1];
        uploadFile({
          file: {
            name: file.name,
            content: base64File,
            type: file.type,
            file_type: documentType
          }
        });
      };
    },
    [uploadFile, documentType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isLoading,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt']
    }
  });

  const handleClose = () => {
    if (!isLoading) {
      setUploadedFile(null);
      setDocumentType('');
      setError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setError(null);
    setUploadProgress(0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            opacity: 0.5
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            opacity: 0.3
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <CloudUpload sx={{ fontSize: 32, mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              Upload File
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Drag & drop or browse to upload your file
            </Typography>
          </Box>
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 4 }}>
        {!uploadedFile ? (
          <Fade in={true}>
            <Box>
              {/* File Type Selection */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Select File Type
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
                {fileTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = documentType === type.value;
                  return (
                    <Chip
                      key={type.value}
                      icon={<Icon />}
                      label={type.label}
                      onClick={() => setDocumentType(type.value)}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        height: 40,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        ...(isSelected && {
                          background: `linear-gradient(45deg, ${fileTypeColors[type.value]}22, ${fileTypeColors[type.value]}11)`,
                          borderColor: fileTypeColors[type.value],
                          color: fileTypeColors[type.value],
                          '& .MuiChip-icon': {
                            color: fileTypeColors[type.value]
                          }
                        })
                      }}
                    />
                  );
                })}
              </Stack>

              {/* Enhanced Dropzone */}
              <Paper
                {...getRootProps()}
                elevation={0}
                sx={{
                  border: `3px dashed ${
                    isDragActive 
                      ? theme.palette.primary.main 
                      : documentType 
                        ? fileTypeColors[documentType] || theme.palette.divider
                        : theme.palette.divider
                  }`,
                  borderRadius: 4,
                  p: 6,
                  textAlign: 'center',
                  cursor: documentType ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: isDragActive 
                    ? `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`
                    : documentType
                      ? `linear-gradient(135deg, ${fileTypeColors[documentType]}08, ${fileTypeColors[documentType]}04)`
                      : 'rgba(0,0,0,0.02)',
                  opacity: documentType ? 1 : 0.6,
                  '&:hover': documentType ? {
                    borderColor: fileTypeColors[documentType] || theme.palette.primary.main,
                    background: `linear-gradient(135deg, ${fileTypeColors[documentType] || theme.palette.primary.main}12, ${fileTypeColors[documentType] || theme.palette.primary.main}06)`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  } : {}
                }}
              >
                <input {...getInputProps()} />
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${fileTypeColors[documentType] || '#667eea'}22, ${fileTypeColors[documentType] || '#764ba2'}11)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CloudUpload 
                    sx={{ 
                      fontSize: 40, 
                      color: fileTypeColors[documentType] || theme.palette.primary.main,
                      opacity: 0.8
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {isDragActive ? 'Drop your file here' : 
                   documentType ? 'Drag & drop or click to browse' : 'Select a file type first'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Supported: JPEG, PNG, PDF, TXT • Max size: 4.5MB
                </Typography>
                {documentType && (
                  <Chip
                    icon={React.createElement(fileTypes.find(t => t.value === documentType)?.icon || Folder)}
                    label={`Selected: ${fileTypes.find(t => t.value === documentType)?.label}`}
                    size="small"
                    sx={{
                      background: `linear-gradient(45deg, ${fileTypeColors[documentType]}22, ${fileTypeColors[documentType]}11)`,
                      borderColor: fileTypeColors[documentType],
                      color: fileTypeColors[documentType]
                    }}
                  />
                )}
              </Paper>

              {/* Upload Progress */}
              {isLoading && (
                <Fade in={true}>
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        Uploading... {Math.round(uploadProgress)}%
                      </Typography>
                      <CircularProgress size={20} />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          background: `linear-gradient(90deg, ${fileTypeColors[documentType] || theme.palette.primary.main}, ${fileTypeColors[documentType] || theme.palette.primary.main}88)`
                        }
                      }}
                    />
                  </Box>
                </Fade>
              )}

              {/* Error Display */}
              {error && (
                <Slide direction="up" in={true}>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 3,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        fontSize: 24
                      }
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}
            </Box>
          </Fade>
        ) : (
          /* Success State */
          <Fade in={true}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)' },
                    '70%': { boxShadow: '0 0 0 20px rgba(76, 175, 80, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
                  }
                }}
              >
                <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#4CAF50' }}>
                Upload Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Your file has been uploaded and is ready to use
              </Typography>

              {renderFilePreview(uploadedFile.url, uploadedFile.name)}
              
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    color: theme.palette.primary.main,
                    fontWeight: 500
                  }}
                >
                  {uploadedFile.url.split('/').pop()}
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>

      {/* Actions */}
      <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={isLoading}
          sx={{ 
            borderRadius: 2,
            minWidth: 100,
            fontWeight: 500
          }}
        >
          {uploadedFile ? 'Done' : 'Cancel'}
        </Button>
        {uploadedFile && (
          <Button
            onClick={handleReset}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              minWidth: 140,
              fontWeight: 500,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Upload Another
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default UploadFileDialog;